"use client";
import { jsx } from "react/jsx-runtime";
import { isBD } from "../../../../utils/businessType.lib.js";
import { useCriteo } from "../../../../utils/criteo/CriteoProvider.js";
import { findCriteoPlacement } from "../../../../utils/criteo/utils.js";
import React, { useMemo } from "react";
import { SpaceLg } from "@nxt-costco-com/forge-design-tokens";
import "../../../Chrome/BrowseContext/ContextProvider.js";
import { useBrowseContext } from "../../../Chrome/BrowseContext/hook.js";
import { CostcoBox } from "../../../Util/CostcoBox.js";
import { useCheckScreen } from "../../../../hooks/useCheckScreen/index.js";
import { CriteoButterflyDisplay } from "./CriteoButterflyDisplay.js";
import { CriteoCarousel } from "./CriteoCarousel.js";
import { CriteoCommerceDisplay } from "./CriteoCommerceDisplay.js";
import { CriteoVideoSpotlight } from "./CriteoVideoSpotlight.js";
import { useCriteoAnalytics } from "./utils.js";
const COMPONENT_NAME = "CriteoAdSet";
function CriteoAdSetComponent({
  contentStackEntry = {},
  locale,
  titleProps,
  isSearchPage,
  isCategoryPage,
  maxSlides,
  maxSlidesBySize,
  productTileSx,
  carouselContainerSx,
  sx,
  isProductDetailsPage = false,
  isTargetingEnabled
}) {
  const {
    butterflyData,
    butterflyError,
    commerceData,
    commerceError,
    isLoadingCriteoData,
    recommendedProductData,
    recommendedProductError,
    searchPageData,
    searchPageError,
    sponsoredProductData,
    sponsoredProductError,
    videoSpotlightData,
    videoSpotlightError
  } = useCriteo();
  const { isMobile } = useCheckScreen();
  const { criteo_placement_name } = (contentStackEntry == null ? void 0 : contentStackEntry.third_party_curation) || {};
  const isSearchOrCategoryPage = isSearchPage || isCategoryPage;
  const isSearchCarousel = isSearchOrCategoryPage && (criteo_placement_name === "Carousel" || criteo_placement_name === "SearchCarousel" || criteo_placement_name === "SearchCarouselTop" || criteo_placement_name === "carousel" || criteo_placement_name === "carouseltop");
  const isCarousel = !isSearchOrCategoryPage && criteo_placement_name === "Carousel";
  const isSpecialtyCarousel = criteo_placement_name === "SpecialtyCarousel";
  const isCommerce = criteo_placement_name === "CommerceDisplay" || criteo_placement_name === "SearchCommerce";
  const isButterfly = criteo_placement_name === "SearchButterfly" || criteo_placement_name === "CategoryButterfly";
  const isVideoSpotlight = criteo_placement_name === "VideoSpotlight";
  let placement = sponsoredProductData == null ? void 0 : sponsoredProductData.placement;
  if (isSpecialtyCarousel) {
    placement = recommendedProductData == null ? void 0 : recommendedProductData.placement;
  } else if (!isSearchOrCategoryPage && isCommerce) {
    placement = commerceData == null ? void 0 : commerceData.placement;
  } else if (isVideoSpotlight && isProductDetailsPage || isVideoSpotlight && !isSearchOrCategoryPage) {
    placement = videoSpotlightData == null ? void 0 : videoSpotlightData.placement;
  } else if (isSearchOrCategoryPage) {
    let placementName;
    if (isSearchCarousel) {
      placementName = criteo_placement_name === "Carousel" || criteo_placement_name === "SearchCarousel" || criteo_placement_name === "carousel" ? "carousel" : "carouseltop";
    } else if (isCommerce) {
      placementName = "CommerceDisplay";
    } else if (isButterfly) {
      placementName = "Butterfly1";
    } else if (isVideoSpotlight) {
      placementName = "Video";
    }
    const basePlacementKey = isCategoryPage ? "viewCategory" : isBD() ? "viewSearchResult" : "viewSearchResults";
    const placementKey = `${basePlacementKey}_API_${isMobile ? "mobile" : "desktop"}-${placementName}`;
    placement = findCriteoPlacement(
      placementKey,
      searchPageData == null ? void 0 : searchPageData.placements
    );
  }
  const products = placement == null ? void 0 : placement.products;
  const format = placement == null ? void 0 : placement.format;
  const analytics = useCriteoAnalytics({ placement, products });
  const ParentSKUs = useMemo(
    () => (products == null ? void 0 : products.map(({ ParentSKU }) => ParentSKU)) || [],
    [products]
  );
  if (!contentStackEntry) {
    console.error("Missing Content Stack Entry");
    return null;
  }
  if (!isTargetingEnabled) {
    return null;
  }
  if (isLoadingCriteoData) {
    return null;
  }
  if (isCarousel && (sponsoredProductError || !sponsoredProductData)) {
    if (sponsoredProductError) {
      console.error(
        "Error loading sponsored product data",
        sponsoredProductError
      );
    }
    return null;
  } else if (isSpecialtyCarousel && (recommendedProductError || !recommendedProductData)) {
    if (sponsoredProductError) {
      console.error(
        "Error loading specialty sponsored product data",
        sponsoredProductError
      );
    }
    return null;
  } else if (!isSearchOrCategoryPage && isCommerce && (commerceError || !commerceData)) {
    if (sponsoredProductError) {
      console.error(
        "Error loading specialty sponsored product data",
        sponsoredProductError
      );
    }
    return null;
  } else if (!isSearchOrCategoryPage && isButterfly && (butterflyError || !butterflyData)) {
    if (sponsoredProductError) {
      console.error(
        "Error loading specialty sponsored product data",
        sponsoredProductError
      );
    }
    return null;
  } else if (!isSearchOrCategoryPage && isVideoSpotlight && (videoSpotlightError || !videoSpotlightData)) {
    if (sponsoredProductError) {
      console.error(
        "Error loading specialty sponsored product data for Video Spotlight",
        sponsoredProductError
      );
    }
    return null;
  } else if (isSearchOrCategoryPage && (searchPageError || !searchPageData)) {
    if (searchPageError) {
      console.error("Error loading search page adset", searchPageError);
    }
    return null;
  } else if (!sponsoredProductData && !recommendedProductData && !commerceData && !butterflyData && !searchPageData && !videoSpotlightData) {
    return null;
  }
  if (!products || !placement) {
    return null;
  }
  if (isCommerce && (placement == null ? void 0 : placement.format) !== "sponsored_products") {
    return /* @__PURE__ */ jsx(
      CostcoBox,
      {
        sx: {
          width: `calc(100% - ${SpaceLg})`
        },
        children: /* @__PURE__ */ jsx(
          CriteoCommerceDisplay,
          {
            ParentSKUs,
            analytics,
            contentStackEntry,
            format,
            locale,
            placement,
            productTileSx,
            products,
            sx,
            titleProps
          }
        )
      }
    );
  } else if (isButterfly && (placement == null ? void 0 : placement.format) !== "sponsored_products") {
    return /* @__PURE__ */ jsx(
      CriteoButterflyDisplay,
      {
        ParentSKUs,
        analytics,
        contentStackEntry,
        format,
        locale,
        placement,
        productTileSx,
        products,
        sx,
        titleProps
      }
    );
  } else if (isVideoSpotlight && (placement == null ? void 0 : placement.format) !== "sponsored_products") {
    return /* @__PURE__ */ jsx(
      CostcoBox,
      {
        sx: isProductDetailsPage ? { ...sx } : {
          display: "flex",
          justifyContent: "center",
          width: !isMobile ? `calc(100% - ${SpaceLg})` : void 0
        },
        children: /* @__PURE__ */ jsx(
          CriteoVideoSpotlight,
          {
            ParentSKUs,
            analytics,
            contentStackEntry,
            format,
            locale,
            placement,
            productTileSx,
            products,
            sx: isProductDetailsPage ? {} : sx,
            titleProps,
            variant: isSearchPage ? "rail" : "default",
            isPdp: isProductDetailsPage
          }
        )
      }
    );
  } else {
    return /* @__PURE__ */ jsx(
      CostcoBox,
      {
        sx: {
          width: isProductDetailsPage ? "100%" : `calc(100% - ${SpaceLg})`
        },
        children: /* @__PURE__ */ jsx(
          CriteoCarousel,
          {
            ParentSKUs,
            analytics,
            carouselContainerSx,
            contentStackEntry,
            isProductDetailsPage,
            locale,
            maxSlides,
            maxSlidesBySize,
            placement,
            productTileSx,
            products,
            sx,
            titleProps
          }
        )
      }
    );
  }
}
const MemoizedCriteoAdSetComponent = React.memo(CriteoAdSetComponent);
const CriteoAdSet = function CriteoAdSet2(props) {
  const { thirdPartyInfo } = useBrowseContext();
  const { consentManagement } = thirdPartyInfo;
  const { isTargetingEnabled } = consentManagement || {};
  return /* @__PURE__ */ jsx(
    MemoizedCriteoAdSetComponent,
    {
      isTargetingEnabled,
      ...props
    }
  );
};
CriteoAdSet.displayName = COMPONENT_NAME;
export {
  CriteoAdSet
};
